import { observer } from 'mobx-react-lite'
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Area,
} from 'recharts'
import { CALLS_CHART_METRICS_CONFIG } from 'entities/Analytics/lib/constants'
import type { CallsChartDataStore } from 'pages/analytics/pages/calls/ui/Chart/store/callsChartDataStore'
import {
  calculateYAxisWidth,
  getMaxValueFromMetrics,
} from 'pages/analytics/lib/calculateYAxisWidth'
import styles from 'pages/analytics/pages/calls/ui/Chart/ui/styles.module.scss'

interface CallsLineChartProps {
  chartDataStore: CallsChartDataStore
  hiddenMetrics?: Set<string>
}

export const CallsLineChart = observer(
  ({ chartDataStore, hiddenMetrics = new Set() }: CallsLineChartProps) => {
    const { rechartsData } = chartDataStore

    const maxValue = getMaxValueFromMetrics<Record<string, unknown>>(
      rechartsData as unknown as Record<string, unknown>[],
      CALLS_CHART_METRICS_CONFIG,
      hiddenMetrics
    )
    const yAxisWidth = calculateYAxisWidth(maxValue)

    const totalMetrics = Object.keys(CALLS_CHART_METRICS_CONFIG).length
    const isOneVisibleMetric = hiddenMetrics.size === totalMetrics - 1
    const visibleMetricColor = isOneVisibleMetric
      ? Object.entries(CALLS_CHART_METRICS_CONFIG).find(([key]) => !hiddenMetrics.has(key))?.[1]
          ?.color
      : ''

    return (
      <div className={styles.chartContainer}>
        <ResponsiveContainer width='100%' height={300}>
          <ComposedChart data={rechartsData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            {isOneVisibleMetric && (
              <defs>
                <linearGradient id='colorGradient' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={visibleMetricColor} stopOpacity={0.3} />
                  <stop offset='100%' stopColor={visibleMetricColor} stopOpacity={0.05} />
                </linearGradient>
              </defs>
            )}
            <CartesianGrid strokeDasharray='3 3' vertical={false} />
            <XAxis dataKey='name' interval='preserveStartEnd' minTickGap={1} />
            <YAxis width={yAxisWidth} />
            <Tooltip />
            {Object.entries(CALLS_CHART_METRICS_CONFIG).map(([key, config]) => {
              if (hiddenMetrics.has(key)) return null

              return (
                <g key={key}>
                  {isOneVisibleMetric && (
                    <Area
                      type='monotone'
                      dataKey={key}
                      stroke={config.color}
                      fill='url(#colorGradient)'
                      strokeWidth={0}
                      tooltipType='none'
                    />
                  )}
                  <Line
                    type='monotone'
                    dataKey={key}
                    stroke={config.color}
                    strokeWidth={2}
                    name={config.name}
                    dot={false}
                  />
                </g>
              )
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    )
  }
)
